import React from 'react';
import { TFunction } from 'i18next';
import {
    Button,
    createTableColumn,
    TableCellLayout,
    TableColumnDefinition,
    TableColumnId,
    TableColumnSizingOptions,
    Tooltip,
} from '@fluentui/react-components';
import {
    AirplaneTakeOffRegular,
    ArrowRoutingRegular,
    bundleIcon,
    CalendarCheckmarkRegular,
    Chat24Filled,
    Chat24Regular,
    EyeRegular,
    LuggageRegular,
    LinkMultipleRegular,
    PeopleRegular,
    PersonAdd24Filled,
    PersonAdd24Regular,
    SendPerson24Filled,
    SendPerson24Regular,
    StarRegular,
    GlobeLocation24Regular
} from '@fluentui/react-icons';
import { FlightTeam } from '../../../model';
import { formatFlightTime } from '../../../utils/formatters';
import { useDepartureBoardPageStyles } from '../DepartureBoardPage.styles';
import {
    getBaggageStatus,
    getConnectionStatus,
    getDepartureStatusTooltipContent,
    getMisconnectedCustomersStatus,
} from '../utils/departureBoardStatusUtils';
import { CustomerNotificationStatus } from '../../../store/features/departureBoard/departureBoardSlice';
import { getHaulTypeLabels } from '../utils/departureBoardFiltersUtils';

type DepartureBoardPageStyles = ReturnType<typeof useDepartureBoardPageStyles>;

export type DepartureBoardTableItem = FlightTeam & { selected: boolean };

export const unsortableColumns = new Set<TableColumnId>([
    'actions',
    'misconnectedCustomersStatus',
    'welcomeCardViewCount',
    'brokerConnection/status',
]);

const SendMessageIconBundle = bundleIcon(Chat24Filled, Chat24Regular);
const AddMisconnectedCustomersIconBundle = bundleIcon(
    PersonAdd24Filled,
    PersonAdd24Regular
);
const PostCustomerMessageIconBundle = bundleIcon(
    SendPerson24Filled,
    SendPerson24Regular
);

const renderDepartureStatus = (
    item: FlightTeam,
    t: TFunction,
    styles: DepartureBoardPageStyles
) => {
    const key = `DepartureStatus_${item.departureStatus}`;
    const element = (
        <div className={styles.status}>
            <div
                className={[
                    styles.departureStatusIndicator,
                    item.departureStatus,
                ].join(' ')}
            ></div>
            {t(key)}
        </div>
    );

    const tooltipContent = getDepartureStatusTooltipContent(item);
    if (tooltipContent) {
        return (
            <Tooltip content={tooltipContent} relationship="label" withArrow>
                {element}
            </Tooltip>
        );
    }

    return element;
};

const renderCheckInStatus = (
    item: FlightTeam,
    t: TFunction,
    styles: DepartureBoardPageStyles
) => {
    const status = item.checkIns.length > 0 ? 'Yes' : 'No';
    const statusDisplayName = t(`CheckInStatus_${status}`);

    return (
        <div className={styles.status}>
            <div className={[styles.statusIndicator, status].join(' ')}></div>
            {statusDisplayName}
        </div>
    );
};

const renderBaggageStatus = (
    item: FlightTeam,
    t: TFunction,
    styles: DepartureBoardPageStyles
) => {
    const status: CustomerNotificationStatus = getBaggageStatus(item);
    const statusDisplayName = t(`CustomerNotificationStatus_${status}`);

    return (
        <div className={styles.status}>
            <div className={[styles.statusIndicator, status].join(' ')}></div>
            {statusDisplayName}
        </div>
    );
};

const renderMisconnectedCustomersStatus = (
    item: FlightTeam,
    t: TFunction,
    styles: DepartureBoardPageStyles
) => {
    const status: CustomerNotificationStatus = getMisconnectedCustomersStatus(item);

    const statusDisplayName = t(`CustomerNotificationStatus_${status}`);

    return (
        <div className={styles.status}>
            <div className={[styles.statusIndicator, status].join(' ')}></div>
            {statusDisplayName}
        </div>
    );
};

const renderConnectionStatus = (
    item: FlightTeam,
    t: TFunction,
    styles: DepartureBoardPageStyles
) => {
    const status = getConnectionStatus(item);

    const statusDisplayName = t(`BrokerConnectionStatus_${status}`);

    return (
        <div className={styles.status}>
            <div className={[styles.connectionStatusIndicator, status].join(' ')}></div>
            {statusDisplayName}
        </div>
    );
};

export const createDepartureBoardColumns = ({
    t,
    styles,
    openSendMessageToTeamDialog,
    openAddMisconnectedCustomersDialog,
    openCustomerMessageDialog,
}: {
    t: TFunction;
    styles: DepartureBoardPageStyles;
    openSendMessageToTeamDialog: (item: FlightTeam) => void;
    openAddMisconnectedCustomersDialog: (item: FlightTeam) => void;
    openCustomerMessageDialog: (flightTeamId: string) => void;
}): TableColumnDefinition<DepartureBoardTableItem>[] => {
    const haulTypeLabels: Record<string, string> = getHaulTypeLabels(t);

    return [
        createTableColumn({
            columnId: 'iataFlightIdentifier',
            renderHeaderCell: () => (
                <TableCellLayout media={<AirplaneTakeOffRegular />}>
                    {t('TableHeader_Flight')}
                </TableCellLayout>
            ),
            renderCell: (item) => (
                <TableCellLayout>{item.iataFlightIdentifier}</TableCellLayout>
            ),
        }),
        createTableColumn({
            columnId: 'departureAirportIata,arrivalAirportIata',
            renderHeaderCell: () => (
                <TableCellLayout media={<ArrowRoutingRegular />}>
                    {t('TableHeader_Airport')}
                </TableCellLayout>
            ),
            renderCell: (item) => (
                <TableCellLayout>{`${item.departureAirportIata} - ${item.arrivalAirportIata}`}</TableCellLayout>
            ),
        }),
        createTableColumn({
            columnId: 'scheduledDepartureTimeUtc',
            renderHeaderCell: () => (
                <TableCellLayout media={<CalendarCheckmarkRegular />}>
                    {t('TableHeader_DepartureTime')}
                </TableCellLayout>
            ),
            renderCell: (item) => (
                <TableCellLayout>{`${item.scheduledDepartureTime
                    ? formatFlightTime(item.scheduledDepartureTime)
                    : formatFlightTime(item.scheduledDepartureTimeUtc, true)
                    }`}</TableCellLayout>
            ),
        }),
        createTableColumn({
            columnId: 'scheduledArrivalTimeUtc',
            renderHeaderCell: () => (
                <TableCellLayout media={<CalendarCheckmarkRegular />}>
                    {t('TableHeader_ArrivalTime')}
                </TableCellLayout>
            ),
            renderCell: (item) => (
                <TableCellLayout>{`${item.scheduledArrivalTime
                    ? formatFlightTime(item.scheduledArrivalTime)
                    : formatFlightTime(item.scheduledArrivalTimeUtc, true)
                    }`}</TableCellLayout>
            ),
        }),
        createTableColumn({
            columnId: 'departureStatus',
            renderHeaderCell: () => (
                <TableCellLayout media={<StarRegular />}>
                    {t('TableHeader_DepartureStatus')}
                </TableCellLayout>
            ),
            renderCell: (item) => <TableCellLayout>{renderDepartureStatus(item, t, styles)}</TableCellLayout>,
        }),
        createTableColumn({
            columnId: 'haulType',
            renderHeaderCell: () => <TableCellLayout media={<GlobeLocation24Regular />}>{t('TableHeader_HaulType', 'SH/LH')}</TableCellLayout>,
            renderCell: (item) => (
                <TableCellLayout>
                    {haulTypeLabels[item.haulType ?? ''] ?? '-'}
                </TableCellLayout>
            ),
        }),
        createTableColumn({
            columnId: 'checkIns/$count',
            renderHeaderCell: () => <TableCellLayout>{t('TableHeader_CheckInStatus')}</TableCellLayout>,
            renderCell: (item) => <TableCellLayout>{renderCheckInStatus(item, t, styles)}</TableCellLayout>,
        }),
        createTableColumn({
            columnId: 'bags/$count',
            renderHeaderCell: () => (
                <TableCellLayout media={<LuggageRegular />}>
                    {t('TableHeader_DelayedBaggage')}
                </TableCellLayout>
            ),
            renderCell: (item) => <TableCellLayout>{renderBaggageStatus(item, t, styles)}</TableCellLayout>,
        }),
        createTableColumn({
            columnId: 'brokerConnection/status',
            renderHeaderCell: () => (
                <TableCellLayout media={<LinkMultipleRegular />}>
                    {t('TableHeader_ConnectionStatus')}
                </TableCellLayout>
            ),
            renderCell: (item) => <TableCellLayout>{renderConnectionStatus(item, t, styles)}</TableCellLayout>,
        }),
        createTableColumn({
            columnId: 'misconnectedCustomersStatus',
            renderHeaderCell: () => (
                <TableCellLayout media={<PeopleRegular />}>
                    {t('TableHeader_MisconnectedCustomers')}
                </TableCellLayout>
            ),
            renderCell: (item) => (
                <TableCellLayout>{renderMisconnectedCustomersStatus(item, t, styles)}</TableCellLayout>
            ),
        }),
        createTableColumn({
            columnId: 'welcomeCardViewCount',
            renderHeaderCell: () => <></>,
            renderCell: (item) => (
                <TableCellLayout
                    media={
                        <Tooltip
                            content={t('Tooltip_WelcomeCardViewCount')}
                            relationship="label"
                            withArrow
                        >
                            <EyeRegular />
                        </Tooltip>
                    }
                >
                    {item.welcomeCardViewCount.toString()}
                </TableCellLayout>
            ),
        }),
        createTableColumn({
            columnId: 'actions',
            renderHeaderCell: () => <></>,
            renderCell: (item) => (
                <TableCellLayout>
                    <Tooltip
                        content={t('Button_SendMessageToTeam_Tooltip')!}
                        relationship="label"
                        withArrow
                    >
                        <Button
                            appearance="transparent"
                            icon={<SendMessageIconBundle />}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                openSendMessageToTeamDialog(item);
                            }}
                        />
                    </Tooltip>
                    <Tooltip
                        content={t('Button_AddMisconnectedCustomers_Tooltip')!}
                        relationship="label"
                        withArrow
                    >
                        <Button
                            appearance="transparent"
                            icon={<AddMisconnectedCustomersIconBundle />}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                openAddMisconnectedCustomersDialog(item);
                            }}
                        />
                    </Tooltip>
                    <Tooltip
                        content={t('Button_PostCustomerMessage_Tooltip')}
                        relationship="label"
                        withArrow
                    >
                        <Button
                            appearance="transparent"
                            icon={<PostCustomerMessageIconBundle />}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                openCustomerMessageDialog(item.id);
                            }}
                        />
                    </Tooltip>
                </TableCellLayout>
            ),
        }),
    ];
};

export const createDepartureBoardColumnSizingOptions = (): TableColumnSizingOptions => ({
    iataFlightIdentifier: { minWidth: 90 },
    'departureAirportIata,arrivalAirportIata': { minWidth: 80 },
    scheduledDepartureTimeUtc: { minWidth: 130 },
    scheduledArrivalTimeUtc: { minWidth: 130 },
    departureStatus: { minWidth: 80 },
    'checkIns/$count': { minWidth: 75 },
    'bags/$count': { minWidth: 160 },
    welcomeCardViewCount: { minWidth: 75 },
    misconnectedCustomersStatus: { minWidth: 160 },
    'brokerConnection/status': { minWidth: 80 },
    actions: { minWidth: 40 },
});
